<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

/*
 * RM-205 Phase 3 — landing page for the phone-verification link
 * (GET /auth/verify-phone?token=… redirects here with ?status=…).
 *
 * Public: a member may open the emailed link on a phone where they are not
 * logged in, and a logged-in member must not be bounced away from the
 * confirmation. One message, one action.
 */

const route = useRoute()
const status = computed(() => String(route.query.status || 'error'))

const COPY = {
  ok: {
    title: 'Your number is confirmed',
    body: 'RM just sent you a welcome text - save that number. Text it anytime, for example "table for 2 at <Favorite Restaurant> this Friday at 7pm", and RM answers there.',
  },
  restaurant_ok: {
    title: 'Your number is confirmed',
    body: 'RM just sent this number a welcome text. RM will text it about RM members\' reservation requests and bookings at your restaurant; reply to any text to reach the RM team, or reply STOP to opt out.',
  },
  // RM-387 (Kapil 10/5): a link that already confirmed the number. Tapping it
  // again used to say "expired", which read as if something had gone wrong.
  already: {
    title: 'Your number is activated',
    body: 'This link was already used to confirm your number, so you\'re all set. Text RM anytime.',
  },
  // A link RM can't match: it ran out unused, or a newer invitation replaced
  // it. A member enrolled before RM-387 lands here too, so it says what to do
  // in both cases and never just "expired".
  expired: {
    title: 'This link is no longer active',
    body: 'If you\'ve already confirmed your number, you\'re all set and can text RM anytime. If not, ask RM for a new invitation.',
  },
  invalid: {
    title: 'This link isn\'t valid',
    body: 'It may have already been used. Text RM AI again and we\'ll email you a fresh link.',
  },
  duplicate: {
    title: 'This number is on another RM account',
    body: 'A number can only be verified on one account. The RM team has been notified and will sort it out — no action needed from you.',
  },
  error: {
    title: 'Something went wrong',
    body: 'We couldn\'t confirm your number. Text RM AI again to get a fresh link, or contact RM if it keeps happening.',
  },
}

const copy = computed(() => COPY[status.value] || COPY.error)
</script>

<template>
  <div class="phone-verified">
    <div class="phone-verified__card">
      <h1 class="phone-verified__title">
        {{ copy.title }}
      </h1>
      <p class="phone-verified__body">
        {{ copy.body }}
      </p>
      <VBtn
        to="/membership"
        color="primary"
        size="large"
        class="phone-verified__cta"
      >
        Open the RM app
      </VBtn>
    </div>
  </div>
</template>

<style scoped>
.phone-verified {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
}

.phone-verified__card {
  max-width: 480px;
  text-align: center;
}

.phone-verified__title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 12px;
}

.phone-verified__body {
  font-size: 16px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin: 0 0 24px;
}

.phone-verified__cta {
  min-width: 200px;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
